import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextTooltip } from "@transfr-inc/dashboard-components";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@transfr-inc/dashboard-components/forms";

import { LOCAL_STORAGE_KEYS, useLocalStorage } from "@lib/hooks";

import { DropdownFilter } from "@components/common";
import {
  DocumentsDropdownFilter,
  DocumentsSearchbox,
} from "@views/curriculum-resources/components";
import { FilterEvent } from "@views/curriculum-resources/tracking";
import {
  FILTER_DEFINITIONS,
  getFiltersData,
} from "@views/curriculum-resources/utils";

import "./curriculum-resources.toolbar.scss";

const defaultFilters = {
  resourceIds: undefined,
  activity: undefined,
  simulation: undefined,
  estimatedTime: undefined,
  industry: undefined,
  term: "",
};

export const CurriculumResourcesToolbar = ({
  className,
  data,
  onFiltersChange,
  onDownloadAll,
  isDownloadingAll,
  hasDocuments,
  product,
  tracking,
}) => {
  const [documentsFiltersStorage, setDocumentsFiltersStorage] = useLocalStorage(
    LOCAL_STORAGE_KEYS.documentsFilters,
    {
      defaultValue: {
        [product.name]: defaultFilters,
      },
    }
  );

  const [filters, setFilters] = useState(
    documentsFiltersStorage[product?.name] ?? defaultFilters
  );

  const trackFilterEvent = (name, value) => {
    if (value) {
      tracking?.trackEvent(new FilterEvent(name, value));
    }
  };

  const onFiltersChangeHandler = (selectedFilters) => {
    const newFilters = { ...filters, ...selectedFilters };
    documentsFiltersStorage[product?.name] = newFilters;

    setDocumentsFiltersStorage(documentsFiltersStorage);
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const filtersData = useMemo(() => getFiltersData(data), [data]);

  useEffect(() => {
    // Apply the filters when the component mounts
    if (Object.values(filters).some((value) => value && value !== "")) {
      onFiltersChangeHandler(filters);
    }
  }, []);

  return (
    <div className={clsx("curriculum-resources-toolbar", className)}>
      <div className="curriculum-resources-toolbar-search">
        <DocumentsSearchbox
          data={filtersData.documents}
          defaultTerm={filters.term}
          onSearchTermChange={({ event, data }) => {
            onFiltersChangeHandler(data);
            tracking?.trackEvent(event);
          }}
          filters={filters}
        />
      </div>
      <div className="curriculum-resources-toolbar-bottom">
        <div className="curriculum-resources-toolbar-filters">
          {(product.filters ?? []).map((filterKey) => {
            const filterDefinition = FILTER_DEFINITIONS[filterKey];

            if (filterDefinition) {
              const onOptionSelected = (value) => {
                onFiltersChangeHandler({ [filterDefinition.key]: value });
                trackFilterEvent(
                  filterDefinition.trackName,
                  filterDefinition.trackValue(value)
                );
              };

              if (filterDefinition.component === "dropdown") {
                return (
                  <DropdownFilter
                    key={filterDefinition.key}
                    data={filtersData[filterDefinition.dataKey]}
                    value={filters[filterDefinition.key]}
                    onOptionSelected={onOptionSelected}
                  />
                );
              }

              return (
                <DocumentsDropdownFilter
                  key={filterDefinition.key}
                  className={filterDefinition.className}
                  data={filtersData[filterDefinition.dataKey]}
                  value={filters[filterDefinition.key]}
                  placeholder={filterDefinition.placeholder}
                  formatter={filterDefinition.formatter}
                  onOptionSelected={onOptionSelected}
                />
              );
            }
          })}
        </div>
        <TextTooltip text="Download zip of all files">
          <Button
            className="download-all-button"
            onClick={onDownloadAll}
            disabled={isDownloadingAll || !hasDocuments}
            primary
          >
            <FontAwesomeIcon
              icon={
                isDownloadingAll
                  ? ["fa-solid", "spinner"]
                  : ["fa-light", "download"]
              }
              spin={isDownloadingAll}
            />
            Download All
          </Button>
        </TextTooltip>
      </div>
    </div>
  );
};

CurriculumResourcesToolbar.propTypes = {
  className: PropTypes.string,
  data: PropTypes.array.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onDownloadAll: PropTypes.func,
  isDownloadingAll: PropTypes.bool,
  hasDocuments: PropTypes.bool,
  product: PropTypes.object,
  tracking: PropTypes.object,
};
